export interface RuntimeSessionStreamPayload {
  sessionId?: string;
  handoffToken?: string;
  projectToken?: string;
  diagramId?: string;
  tenantId?: string;
  channel?: string;
  context?: Record<string, unknown>;
}

type RuntimeSessionStreamMessage = {
  type?: string;
  payload?: Record<string, unknown>;
};

type RuntimeSessionConnectMode = 'session.start' | 'session.join';
type RuntimeSessionConnectResult = 'started' | 'joined';
type RuntimeSessionStreamError = Error & { code?: string };

function createRuntimeSessionStreamError(code: string, message: string): RuntimeSessionStreamError {
  const error = new Error(message) as RuntimeSessionStreamError;
  error.code = code;
  return error;
}

function toRuntimeSessionStreamUrl(runtimeUrl: string): string {
  const baseUrl = runtimeUrl.replace(/\/$/, '');
  return `${baseUrl.replace(/^http/i, 'ws')}/session-stream`;
}

export class RuntimeSessionStreamClient {
  private readonly runtimeUrl: string;
  private socket: WebSocket | null = null;
  private pendingConnect: Promise<RuntimeSessionConnectResult> | null = null;
  private connected = false;
  private disconnectRequested = false;

  constructor(runtimeUrl: string) {
    this.runtimeUrl = runtimeUrl;
  }

  connect(payload: RuntimeSessionStreamPayload): Promise<RuntimeSessionConnectResult> {
    if (this.connected && this.socket?.readyState === WebSocket.OPEN) {
      return Promise.resolve('joined');
    }

    if (this.pendingConnect) {
      return this.pendingConnect;
    }

    this.disconnectRequested = false;
    this.pendingConnect = this.connectWithMode('session.start', payload).finally(() => {
      this.pendingConnect = null;
    });

    return this.pendingConnect;
  }

  disconnect(): void {
    this.disconnectRequested = true;
    this.connected = false;

    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }

  private connectWithMode(
    mode: RuntimeSessionConnectMode,
    payload: RuntimeSessionStreamPayload
  ): Promise<RuntimeSessionConnectResult> {
    return new Promise((resolve, reject) => {
      const socket = new WebSocket(toRuntimeSessionStreamUrl(this.runtimeUrl));
      let settled = false;

      const fail = (error: RuntimeSessionStreamError) => {
        if (settled) {
          return;
        }

        settled = true;
        if (this.socket === socket) {
          this.socket = null;
        }
        this.connected = false;
        reject(error);
      };

      const succeed = (result: RuntimeSessionConnectResult) => {
        if (settled) {
          return;
        }

        settled = true;
        this.socket = socket;
        this.connected = true;
        resolve(result);
      };

      socket.onopen = () => {
        if (this.disconnectRequested) {
          fail(createRuntimeSessionStreamError('connection-aborted', 'Runtime session bootstrap was aborted'));
          socket.close();
          return;
        }

        socket.send(JSON.stringify({
          type: mode,
          payload
        }));
      };

      socket.onmessage = (event) => {
        if (typeof event.data !== 'string') {
          return;
        }

        let message: RuntimeSessionStreamMessage;
        try {
          message = JSON.parse(event.data);
        } catch {
          return;
        }

        if (message.type === 'ping') {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({
              type: 'pong',
              payload: { timestamp: Date.now() }
            }));
          }
          return;
        }

        if (message.type === 'session.accepted') {
          succeed(mode === 'session.start' ? 'started' : 'joined');
          return;
        }

        if (message.type === 'session.joined') {
          succeed('joined');
          return;
        }

        if (message.type === 'session.error') {
          const code = typeof message.payload?.code === 'string'
            ? message.payload.code
            : 'session-error';
          const messageText = typeof message.payload?.message === 'string'
            ? message.payload.message
            : 'Unable to connect runtime session';

          if (mode === 'session.start' && code === 'duplicate-session') {
            settled = true;
            this.connected = false;
            if (this.socket === socket) {
              this.socket = null;
            }
            socket.close();
            this.connectWithMode('session.join', payload).then(resolve, reject);
            return;
          }

          fail(createRuntimeSessionStreamError(code, messageText));
        }
      };

      socket.onerror = () => {
        fail(createRuntimeSessionStreamError('connection-failed', 'Unable to connect runtime session'));
      };

      socket.onclose = () => {
        if (!settled) {
          fail(createRuntimeSessionStreamError(
            'connection-closed',
            mode === 'session.start'
              ? 'Runtime session closed before it was created'
              : 'Runtime session closed before it was joined'
          ));
          return;
        }

        if (this.socket === socket) {
          this.socket = null;
          this.connected = false;
        }
      };
    });
  }
}
